import {
	DEFAULT_CURRENCY_CODE,
	DEFAULT_CURRENCY_DISPLAY,
	FORMAT_STYLE_CURRENCY,
	FORMAT_STYLE_DECIMAL,
	FORMAT_STYLE_PERCENT,
	FORMAT_STYLE_UNIT,
	FORMAT_UNIT_PERCENT,
	type CurrencyDisplayOptions,
	type NumberFieldDisplayConfig,
	type NumberFieldDisplayOptions,
} from './types.tsx';

/**
 * This is due to Jira commonly using the POSIX format (using '_' for defining locales),
 * such as in jira/src/packages/platform/utils/constants/src/supported-locales.tsx,
 * whereas the Intl Javascript API requires locales to be in the IETF BCP 47 format.
 */
const coerceBCPLocaleFormat = (locale: string): string => {
	return locale.replace('_', '-');
};

// Converts strings to lower case while also preventing string literal types from being converted
// into the more general `string` type as the String.prototype.toLowerCase() method always returns
// a `string` type, even if the string is a string literal. For example:
// const string = 'CONSTANT'; // type is `'CONSTANT'`;
// string.toLowerCase() // returned type is `string`;
// toLowerCase(string) // returned type is `'constant'`;
const toLowerCase = <T extends string>(inputString: T): Lowercase<T> => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return inputString.toLocaleLowerCase() as Lowercase<T>;
};

const isSupportedCurrencyCode = (currencyCode: string): boolean => {
	// Check if the currency code is supported
	return Intl.supportedValuesOf(toLowerCase(FORMAT_STYLE_CURRENCY)).includes(currencyCode);
};

const isSupportedUnit = (unit: string): boolean => {
	// Check if the unit is supported
	return Intl.supportedValuesOf(toLowerCase(FORMAT_STYLE_UNIT)).includes(unit);
};

export const formatCurrency = (amount: number, config: CurrencyDisplayOptions = {}): string => {
	const {
		currencyCode = DEFAULT_CURRENCY_CODE,
		locale,
		currencyDisplay = DEFAULT_CURRENCY_DISPLAY,
		...rest
	} = config;

	// Check if the currency code is supported. fall back to decimal format if not.
	const formatOptions: Intl.NumberFormatOptions = isSupportedCurrencyCode(currencyCode)
		? {
				style: FORMAT_STYLE_CURRENCY.toLocaleLowerCase(),
				currency: currencyCode,
				currencyDisplay,
				...rest,
			}
		: {
				style: FORMAT_STYLE_DECIMAL.toLocaleLowerCase(),
				...rest,
			};

	const formatter = new Intl.NumberFormat(locale && coerceBCPLocaleFormat(locale), formatOptions);
	return formatter.format(amount ?? 0);
};

export const formatPercent = (amount: number, config: NumberFieldDisplayOptions = {}): string => {
	const { locale, ...rest } = config;
	return new Intl.NumberFormat(locale && coerceBCPLocaleFormat(locale), {
		...rest,
		style: FORMAT_STYLE_UNIT.toLocaleLowerCase(),
		unit: FORMAT_UNIT_PERCENT,
	}).format(amount ?? 0);
};

export const formatUnits = (number: number, config: NumberFieldDisplayOptions = {}): string => {
	const { unit, locale, ...rest } = config;

	// Check if the unit code is supported. fall back to decimal format if not.
	const formatOptions: Intl.NumberFormatOptions =
		unit && isSupportedUnit(unit)
			? {
					style: FORMAT_STYLE_UNIT.toLocaleLowerCase(),
					unit,
					...rest,
				}
			: {
					style: FORMAT_STYLE_DECIMAL.toLocaleLowerCase(),
					...rest,
				};
	const formatter = new Intl.NumberFormat(locale && coerceBCPLocaleFormat(locale), formatOptions);
	return formatter.format(number ?? 0);
};

export const formatDecimal = (number: number, config: NumberFieldDisplayOptions = {}): string => {
	const formatter = new Intl.NumberFormat(config.locale && coerceBCPLocaleFormat(config.locale), {
		style: FORMAT_STYLE_DECIMAL.toLocaleLowerCase(),
		...config,
	});
	return formatter.format(number ?? 0);
};

export const formatNumberField = (
	number: number,
	config: NumberFieldDisplayConfig = { formatStyle: FORMAT_STYLE_DECIMAL },
): string => {
	const { formatStyle, formatUnit, locale } = config;
	switch (formatStyle) {
		case FORMAT_STYLE_CURRENCY:
			return formatUnit
				? formatCurrency(number, { locale, currencyCode: formatUnit })
				: formatDecimal(number, { locale });
		case FORMAT_STYLE_PERCENT:
			return formatPercent(number, { locale });
		case FORMAT_STYLE_UNIT:
			return formatUnit
				? formatUnits(number, { locale, unit: formatUnit })
				: formatDecimal(number, { locale });
		default: // fall back to decimal, we will add more formats in the future
			return formatDecimal(number);
	}
};
